import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { ShieldAlert, Trash2, RotateCcw, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/shared/stores/auth.store';
import { isExecutive } from '@/shared/permissions/identity';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { usePipelineTrashList, usePipelineTrashMutations, type TrashedPipelineRecord } from '../hooks/usePipelineTrash';

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Super Admin only. Everything a "Move to Trash" on a Sales Pipeline record
 * cascaded onto (its client profile, contacts, quotations, proforma,
 * samples, delivery challans — see src/shared/trash/cascade.js) is invisible
 * to everyone else until it is Restored or Permanently Deleted from here.
 * Already-issued GST tax invoices are never part of this — they stay
 * visible to Accounts regardless of a linked deal's trash state.
 */
export function PipelineTrashPage() {
  const { user } = useAuthStore();
  const allowed = isExecutive(user?.role);

  const { data, isLoading } = usePipelineTrashList();
  const { restore, permanentlyDelete } = usePipelineTrashMutations();
  const [confirmDelete, setConfirmDelete] = useState<TrashedPipelineRecord | null>(null);

  if (!allowed) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <ShieldAlert className="h-10 w-10 text-slate-300" />
        <p className="text-sm font-semibold text-slate-600">Only a Super Admin can open the Trash folder.</p>
      </div>
    );
  }

  const handleRestore = (record: TrashedPipelineRecord) => {
    restore.mutate(record.id, {
      onSuccess: () => toast.success(`"${record.title || record.name}" restored.`),
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to restore the record.'),
    });
  };

  const handlePermanentDelete = () => {
    if (!confirmDelete) return;
    const record = confirmDelete;
    permanentlyDelete.mutate(record.id, {
      onSuccess: () => toast.success(`"${record.title || record.name}" permanently deleted.`),
      onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to permanently delete the record.'),
    });
    setConfirmDelete(null);
  };

  const items = data?.items || [];

  return (
    <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <PageHeader icon={Trash2} eyebrow="Sales" title="Pipeline Trash" description={<>Records here (and everything moving them to Trash cascaded onto — client profile, contacts, quotations,
          proforma, samples, delivery challans) are hidden from everyone else. Already-issued tax invoices are never
          affected. Restore a record to bring it and everything cascaded with it back, or permanently delete it —
          that cannot be undone.</>} />

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-16 text-center text-sm text-slate-500">
          Trash is empty.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Record</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Trashed on</th>
                <th className="px-4 py-3">Trashed by</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((record) => (
                <tr key={record.id}>
                  <td className="px-4 py-3 font-semibold text-slate-800">{record.title || record.name}</td>
                  <td className="px-4 py-3 text-slate-600">{record.contact_name || record.business_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(record.trashedAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{record.trashedBy?.name || record.trashedBy?.email || '—'}</td>
                  <td className="px-4 py-3 text-slate-500">{record.trashedReason || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleRestore(record)}
                        disabled={restore.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Restore
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(record)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100 transition-colors"
                      >
                        <XCircle className="h-3.5 w-3.5" />
                        Delete Permanently
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Permanently Delete"
        message={`Permanently delete "${confirmDelete?.title || confirmDelete?.name}" and everything Trash moved with it? `
          + 'This cannot be undone.'}
        confirmLabel="Delete Permanently"
        danger
        loading={permanentlyDelete.isPending}
        onConfirm={handlePermanentDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
